import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { businessesService } from '@services/businesses.service';
import type { Business } from '@apptypes/index';

function apiMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown };
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
  }
  return fallback;
}

const input = 'w-full rounded-xl border border-[var(--border-default)] bg-[var(--bg-base)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none';

export function BusinessEditModal({ business, onClose, onSaved }: {
  business: Business; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: business.name ?? '',
    description: business.description ?? '',
    email: business.email ?? '',
    phone: business.phone ?? '',
    businessCategory: business.businessCategory ?? '',
    taxType: business.taxType ?? '',
    kraPin: business.kraPin ?? '',
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: () => businessesService.adminUpdate(business.id, form),
    onSuccess: () => { toast.success('Business updated.'); onSaved(); },
    onError: (e) => toast.error(apiMessage(e, 'Could not update the business.')),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="mt-4 w-full max-w-lg rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-[var(--text-primary)]">Edit business</h3>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <Field label="Business name"><input value={form.name} onChange={set('name')} className={input} /></Field>
          <Field label="Description"><textarea value={form.description} onChange={set('description')} rows={3} className={input} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Phone"><input value={form.phone} onChange={set('phone')} className={input} /></Field>
            <Field label="Email"><input value={form.email} onChange={set('email')} className={input} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Business category">
              <select value={form.businessCategory} onChange={set('businessCategory')} className={input}>
                <option value="">—</option>
                <option value="retail">Retail</option>
                <option value="wholesale">Wholesale</option>
                <option value="services">Services</option>
                <option value="restaurant">Restaurant / Food</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Tax type">
              <select value={form.taxType} onChange={set('taxType')} className={input}>
                <option value="">—</option>
                <option value="vat">VAT</option>
                <option value="tot">TOT</option>
                <option value="exempt">Exempt</option>
              </select>
            </Field>
          </div>
          <Field label="KRA PIN"><input value={form.kraPin} onChange={set('kraPin')} className={input} /></Field>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-[var(--text-muted)]">Cancel</button>
            <button disabled={save.isPending || !form.name.trim()} onClick={() => save.mutate()}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--text-primary)] px-4 py-2 text-sm font-semibold text-[var(--bg-surface)] disabled:opacity-50">
              {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-[13px] font-medium text-[var(--text-secondary)]">{label}</label>
      {children}
    </div>
  );
}
